"use client";
import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// ─── Steps Data ───────────────────────────────────────────────────────────────
const STEPS = [
  {
    translationKey: "leapStepsSection.steps.postOnce",
  },
  {
    translationKey: "leapStepsSection.steps.aiInterviews",
  },
  {
    translationKey: "leapStepsSection.steps.rankedShortlist",
  },
];

// ─── Styled Components ──────────────────────────────────────────────────────
const StepsSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#F7F5FB",
  padding: "90px 0",
  [theme.breakpoints.down("sm")]: {
    padding: "48px 0",
  },
}));

const SectionHeading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: "bold",
  fontSize: "38px",
  color: "#171522",
  textAlign: "center",
  marginBottom: "40px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "24px",
    marginBottom: "28px",
  },
}));

const StepsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "24px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const StepCard = styled(Box)(() => ({
  flex: 1,
  backgroundColor: "#FFFFFF",
  border: "1px solid #ECEAF5",
  borderRadius: "14px",
  padding: "24px",
  boxShadow: "0px 4px 16px rgba(20, 10, 40, 0.04)",
}));

const StepLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "0.5px",
  color: "#9048FB",
  marginBottom: "12px",
}));

const StepTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: 700,
  fontSize: "18px",
  color: "#171522",
  marginBottom: "10px",
}));

const StepDescription = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "14px",
  lineHeight: 1.7,
  color: "#5A5568",
}));

// ─── Component ───────────────────────────────────────────────────────────────
const LeapStepsSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  return (
    <StepsSection>
      <Container maxWidth="lg">
        <SectionHeading variant="h2" isRTL={isRTL}>
          {t("leapStepsSection.heading")}
        </SectionHeading>

        <StepsRow>
          {STEPS.map((step) => (
            <StepCard key={step.translationKey}>
              <StepLabel isRTL={isRTL}>
                {t(`${step.translationKey}.step`)}
              </StepLabel>
              <StepTitle isRTL={isRTL}>
                {t(`${step.translationKey}.title`)}
              </StepTitle>
              <StepDescription isRTL={isRTL}>
                {t(`${step.translationKey}.description`)}
              </StepDescription>
            </StepCard>
          ))}
        </StepsRow>
      </Container>
    </StepsSection>
  );
};

export default LeapStepsSection;